import { useQueryClient } from '@tanstack/react-query';
import { request } from '@/api/apiUtils';
import { actsEntity } from '@/data/entities/acts';
import { fountainScenesEntity } from '@/data/entities/fountainScenes';
import { fragmentsEntity } from '@/data/entities/fragments';
import { getCrossCuttingPrefixes, useCustomMutation } from '@/data/mutations';
import { useRequiredAppSelector, useVersion } from '@/hooks';
import type { IFountainAct, IFountainFragment, IFountainScene, ITag } from '@/types';

type EntityType = 'act' | 'fragment' | 'scene';

const entityEndpointMap: Record<EntityType, string> = {
    act: 'acts',
    fragment: 'fragments',
    scene: 'scenes',
};

interface AttachEntityTagData {
    entityType: EntityType;
    entityId: number;
    addTags?: number[];
    removeTags?: number[];
}

interface OptimisticContext {
    entityType: EntityType;
    queryKey: readonly unknown[];
    previous: unknown;
}

const crossCuttingPrefixes = getCrossCuttingPrefixes('entityTag.attach');

function buildOptimisticTags(currentTags: ITag[], addTags: number[], removeTags: number[]): ITag[] {
    let updated = currentTags.filter(t => !removeTags.includes(t.id));
    for (const tagId of addTags) {
        if (!updated.some(t => t.id === tagId)) {
            updated = [...updated, { id: tagId, name: '', tag_category_id: 0, color: '' } as unknown as ITag];
        }
    }
    return updated;
}

export function useAttachEntityTag() {
    const projectId = useRequiredAppSelector('projectId');
    const version = useVersion();
    const queryClient = useQueryClient();

    return useCustomMutation<AttachEntityTagData, unknown, OptimisticContext>({
        mutationFn: async ({ entityType, entityId, addTags = [], removeTags = [] }) => {
            const endpoint = entityEndpointMap[entityType];
            return request(`projects/${projectId}/${endpoint}/${entityId}?_method=PUT`, {
                method: 'POST',
                body: JSON.stringify({ add_tags: addTags, remove_tags: removeTags }),
            });
        },
        onMutate: async ({ entityType, entityId, addTags = [], removeTags = [] }) => {
            const queryKey =
                entityType === 'act'
                    ? actsEntity.fetchers.list.key({ projectId, version: version })
                    : entityType === 'fragment'
                      ? fragmentsEntity.fetchers.list.key({ projectId, version: version })
                      : fountainScenesEntity.fetchers.list.key({ projectId, version: version });

            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData(queryKey);

            if (entityType === 'act') {
                queryClient.setQueryData<IFountainAct[]>(queryKey, old =>
                    old?.map(a => (a.id === entityId ? { ...a, tags: buildOptimisticTags(a.tags, addTags, removeTags) } : a)),
                );
            } else if (entityType === 'fragment') {
                queryClient.setQueryData<IFountainFragment[]>(queryKey, old =>
                    old?.map(f => (f.id === entityId ? { ...f, tags: buildOptimisticTags(f.tags, addTags, removeTags) } : f)),
                );
            } else {
                queryClient.setQueryData<IFountainScene[]>(queryKey, old =>
                    old?.map(s => (s.id === entityId ? { ...s, tags: buildOptimisticTags(s.tags, addTags, removeTags) } : s)),
                );
            }

            return { entityType, queryKey, previous };
        },
        onError: (_error, _params, ctx) => {
            if (ctx) {
                queryClient.setQueryData(ctx.queryKey, ctx.previous);
            }
        },
        invalidate: crossCuttingPrefixes,
    });
}
